import type {
  ExecutionEnvironmentAuthCapabilities,
  ExecutionEnvironmentCapabilities,
  ExecutionEnvironmentDescriptor,
  ExecutionEnvironmentProjectCapabilities,
  ExecutionEnvironmentRuntimeCapabilities,
  ExecutionEnvironmentRuntimeTopology,
  ExecutionEnvironmentToolCapabilities,
} from "@cmmd-forge/contracts";

export interface NormalizedExecutionEnvironmentCapabilities {
  readonly repositoryIdentity: boolean;
  readonly pullRequests: boolean;
  readonly cloudMode: boolean;
  readonly posthogPublicKey: string | null;
  readonly posthogHost: string | null;
  readonly runtime: ExecutionEnvironmentRuntimeCapabilities;
  readonly auth: ExecutionEnvironmentAuthCapabilities;
  readonly tools: ExecutionEnvironmentToolCapabilities;
  readonly projects: ExecutionEnvironmentProjectCapabilities;
}

function legacyTopology(
  capabilities: ExecutionEnvironmentCapabilities,
): ExecutionEnvironmentRuntimeTopology {
  return capabilities.cloudMode ? "cmmd-cloud-runtime" : "local-web";
}

function defaultRuntimeCapabilities(
  capabilities: ExecutionEnvironmentCapabilities,
): ExecutionEnvironmentRuntimeCapabilities {
  const topology = legacyTopology(capabilities);
  const isCloudRuntime = topology === "cmmd-cloud-runtime";
  return {
    topology,
    controlPlane: !isCloudRuntime,
    executionPlane: true,
    workspaceProxy: false,
  };
}

function defaultAuthCapabilities(
  runtime: ExecutionEnvironmentRuntimeCapabilities,
): ExecutionEnvironmentAuthCapabilities {
  const isCmmdRuntime = runtime.topology === "cmmd-cloud-runtime";
  return {
    cmmd: true,
    ownerBootstrapPromotion: runtime.topology !== "cloud-shell" && !isCmmdRuntime,
    bearerBootstrap: true,
    desktopBootstrap: runtime.topology === "desktop-local",
    providerCredentialBroker: runtime.topology === "cmmd-control-plane",
  };
}

function defaultToolCapabilities(
  runtime: ExecutionEnvironmentRuntimeCapabilities,
): ExecutionEnvironmentToolCapabilities {
  const executionTooling = runtime.executionPlane;
  return {
    agents: executionTooling,
    browser: executionTooling,
    github: executionTooling,
    terminal: executionTooling,
    providerEgress: runtime.topology === "cmmd-control-plane",
  };
}

function defaultProjectCapabilities(
  capabilities: ExecutionEnvironmentCapabilities,
  runtime: ExecutionEnvironmentRuntimeCapabilities,
): ExecutionEnvironmentProjectCapabilities {
  return {
    localProjects: runtime.executionPlane,
    cloudProjects: capabilities.cloudMode,
  };
}

export function normalizeExecutionEnvironmentCapabilities(
  descriptor: ExecutionEnvironmentDescriptor,
): NormalizedExecutionEnvironmentCapabilities {
  const capabilities = descriptor.capabilities;
  const runtime = capabilities.runtime ?? defaultRuntimeCapabilities(capabilities);
  return {
    repositoryIdentity: capabilities.repositoryIdentity,
    pullRequests: capabilities.pullRequests ?? false,
    cloudMode: capabilities.cloudMode,
    posthogPublicKey: capabilities.posthogPublicKey,
    posthogHost: capabilities.posthogHost,
    runtime,
    auth: capabilities.auth ?? defaultAuthCapabilities(runtime),
    tools: capabilities.tools ?? defaultToolCapabilities(runtime),
    projects: capabilities.projects ?? defaultProjectCapabilities(capabilities, runtime),
  };
}

export function environmentSupportsCloudProjects(
  descriptor: ExecutionEnvironmentDescriptor | null | undefined,
): boolean {
  return descriptor
    ? normalizeExecutionEnvironmentCapabilities(descriptor).projects.cloudProjects
    : false;
}

export function environmentSupportsBrowserTools(
  descriptor: ExecutionEnvironmentDescriptor | null | undefined,
): boolean {
  return descriptor ? normalizeExecutionEnvironmentCapabilities(descriptor).tools.browser : false;
}

export function environmentSupportsGitHubTools(
  descriptor: ExecutionEnvironmentDescriptor | null | undefined,
): boolean {
  return descriptor ? normalizeExecutionEnvironmentCapabilities(descriptor).tools.github : false;
}

export function environmentRunsAgents(
  descriptor: ExecutionEnvironmentDescriptor | null | undefined,
): boolean {
  return descriptor ? normalizeExecutionEnvironmentCapabilities(descriptor).tools.agents : false;
}

/**
 * True when the primary server explicitly reports that it is a workspace proxy.
 * Proxy control surfaces have no local workspace, so the SPA skips waiting for
 * a workspace bootstrap snapshot.
 */
export function environmentIsWorkspaceProxy(
  descriptor: ExecutionEnvironmentDescriptor | null | undefined,
): boolean {
  return descriptor
    ? (normalizeExecutionEnvironmentCapabilities(descriptor).runtime.workspaceProxy ?? false)
    : false;
}
